/**
 * @module poh-semaphore-client
 * @version 0.1.0
 * @file A library for interacting this poh-semaphore based services.
*/
'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var identity = require('@semaphore-protocol/identity');
var ethers = require('ethers');
var utils = require('ethers/lib/utils');
var group = require('@semaphore-protocol/group');
var snarkjs = require('snarkjs');
var circomlibjs = require('circomlibjs');
var subgraph = require('@semaphore-protocol/subgraph');

const IDENTITY_SIGNING_MESSAGE = "Sign this message to generate your PoH Semaphore identity";

const config = {
    network: "goerli",
    groupId: "639539",
    treeDepth: 20,
    zeroValue: "0",
};

// Adapted from https://github.com/semaphore-protocol/semaphore.js/blob/main/packages/proof/src/generateProof.ts
function generateSignalHash(signal) {
    return BigInt(ethers.ethers.utils.keccak256(signal)) >> BigInt(8);
}
async function generateSemaphoreProof(identity, group, externalNullifier, signal, snarkArtifacts) {
    const commitment = identity.generateCommitment();
    const index = group.indexOf(commitment);
    if (index === -1) {
        throw new Error("The identity is not part of the group");
    }
    const merkleProof = group.generateProofOfMembership(index);
    if (!snarkArtifacts) {
        snarkArtifacts = {
            wasmFilePath: `https://www.trusted-setup-pse.org/semaphore/${merkleProof.siblings.length}/semaphore.wasm`,
            zkeyFilePath: `https://www.trusted-setup-pse.org/semaphore/${merkleProof.siblings.length}/semaphore.zkey`,
        };
    }
    const { proof, publicSignals } = await snarkjs.groth16.fullProve({
        identityTrapdoor: identity.getTrapdoor(),
        identityNullifier: identity.getNullifier(),
        treePathIndices: merkleProof.pathIndices,
        treeSiblings: merkleProof.siblings,
        externalNullifier,
        signalHash: generateSignalHash(signal),
    }, snarkArtifacts.wasmFilePath, snarkArtifacts.zkeyFilePath);
    return {
        proof,
        publicSignals: {
            merkleRoot: publicSignals[0],
            nullifierHash: publicSignals[1],
            signalHash: publicSignals[2],
            externalNullifier: publicSignals[3],
        },
    };
}

// @ts-ignore
async function generateNullifierConsistencyProof(serviceNullifier, privIdentityNullifier, privRandomNonce) {
    const poseidon = await circomlibjs.buildPoseidon();
    const hash = (x1, x2) => {
        const uint8Arr = poseidon([x1, x2]);
        return poseidon.F.toObject(uint8Arr).toString();
    };
    const identityProxy = hash(serviceNullifier, privIdentityNullifier);
    const externalNullifier = hash(identityProxy, privRandomNonce);
    const nullifierHash = hash(externalNullifier, privIdentityNullifier);
    const input = {
        privIdentityNullifier,
        privRandomNonce,
        identityProxy,
        serviceNullifier,
        externalNullifier,
        nullifierHash,
    };
    const wasmFile = `${window.location.origin}/NullifierConsistency.wasm`;
    const zkeyFilePath = `${window.location.origin}/circuit_final.zkey`;
    const { proof } = await snarkjs.groth16.fullProve(input, wasmFile, zkeyFilePath);
    return {
        proof,
        identityProxy,
        externalNullifier,
        nullifierHash,
    };
}

async function getMembers(skipZeros = false) {
    const subgraph$1 = new subgraph.Subgraph(config.network);
    const group = await subgraph$1.getGroup(config.groupId, {
        members: true,
    });
    const members = group.members;
    if (skipZeros)
        return members.filter((member) => member !== "0");
    return members;
}

function genRandomNumber(numberOfBytes = 31) {
    return ethers.BigNumber.from(utils.randomBytes(numberOfBytes)).toString();
}
function flattenProofAbc(proof) {
    return [
        proof.pi_a[0],
        proof.pi_a[1],
        proof.pi_b[0][1],
        proof.pi_b[0][0],
        proof.pi_b[1][1],
        proof.pi_b[1][0],
        proof.pi_c[0],
        proof.pi_c[1],
    ].map((x) => x.toString(16));
}
async function generateProofPair(signedIdentityMessage, serviceNullifier, signal) {
    const identity$1 = new identity.Identity(signedIdentityMessage);
    const nullifierConsistencyFullProof = await generateNullifierConsistencyProof(serviceNullifier, identity$1.getNullifier().toString(), genRandomNumber());
    const members = await getMembers();
    const group$1 = new group.Group(config.treeDepth, config.zeroValue);
    group$1.addMembers(members);
    const semaphoreFullProof = await generateSemaphoreProof(identity$1, group$1, nullifierConsistencyFullProof.externalNullifier, signal, {
        wasmFilePath: `${window.location.origin}/semaphore.wasm`,
        zkeyFilePath: `${window.location.origin}/semaphore.zkey`,
    });
    const nullifierConsistencyProofAbc = flattenProofAbc(nullifierConsistencyFullProof.proof);
    const semaphoreProofAbc = flattenProofAbc(semaphoreFullProof.proof);
    const { identityProxy, externalNullifier, nullifierHash } = nullifierConsistencyFullProof;
    return {
        nullifierConsistencyProofAbc,
        semaphoreProofAbc,
        identityProxy,
        externalNullifier,
        nullifierHash,
    };
}

async function generateIdentityProxy(serviceNullifier, signedIdentityMessage) {
    const identity$1 = new identity.Identity(signedIdentityMessage);
    const identityNullifier = identity$1.getNullifier();
    const poseidon = await circomlibjs.buildPoseidon();
    const identityProxyUint8Array = poseidon([
        serviceNullifier,
        identityNullifier,
    ]);
    const identityProxy = poseidon.F.toObject(identityProxyUint8Array).toString();
    return identityProxy;
}

function generateIdentityCommitment(signedIdentityMessage) {
    const identity$1 = new identity.Identity(signedIdentityMessage);
    return identity$1.generateCommitment();
}

async function generateProofOfMembership(identityCommitment) {
    const members = await getMembers();
    const group$1 = new group.Group(config.treeDepth, config.zeroValue);
    group$1.addMembers(members);
    const proof = group$1.generateProofOfMembership(group$1.indexOf(BigInt(identityCommitment)));
    return proof;
}

exports.IDENTITY_SIGNING_MESSAGE = IDENTITY_SIGNING_MESSAGE;
exports.config = config;
exports.generateIdentityCommitment = generateIdentityCommitment;
exports.generateIdentityProxy = generateIdentityProxy;
exports.generateProofOfMembership = generateProofOfMembership;
exports.generateProofPair = generateProofPair;
exports.getMembers = getMembers;
